import i18n from '@i18n/i18n';
import { getUserTimezone } from '@utils/dateTime';

/**
 * AI Assistant API client — hits /api/ai/v1/* endpoints.
 *
 * Operator 與 admin 共用，因為後端 controller 用 authenticate_any_token!
 * 接受 operator / user 兩種 cookie token。
 */

const aiFetch = async (path, options = {}) => {
  const url = `/api/ai/v1/${path.replace(/^\//, '')}`;
  const { body, headers: customHeaders, ...rest } = options;

  const headers = {
    'Accept-Language': i18n.language || 'zh',
    'X-Timezone': getUserTimezone(),
    ...customHeaders,
  };

  let requestBody = body;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    requestBody = JSON.stringify(body);
  }

  return fetch(url, {
    credentials: 'include',
    ...rest,
    body: requestBody,
    headers,
  });
};

/**
 * 送出新訊息：建立 / 接續 conversation，由後端 enqueue AiRunWorker 打 Python AI。
 * 立即回傳 IDs，FE 自行 polling getConversation 取結果。
 *
 * @param {{
 *   message: string,
 *   conversationId?: string | null,
 *   assistantType?: string | null,
 *   context?: any,
 *   attachment?: any
 * }} params
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: {
 *     conversation_id?: string,
 *     run_id?: string,
 *     user_message_id?: string,
 *     assistant_message_id?: string
 *   }
 * }>}
 */
export const createAssistantMessage = async ({
  message,
  conversationId = null,
  assistantType = null,
  context = null,
  attachment = null,
}) => {
  const res = await aiFetch('assistant', {
    method: 'POST',
    body: {
      messages: message,
      conversation_id: conversationId,
      assistant_type: assistantType,
      context,
      attachment,
    },
  });
  return res.json();
};

/**
 * 取 conversation + 所有 messages (含 status)。FE polling 用。
 *
 * @param {string} conversationId
 * @returns {Promise<{
 *   success: boolean,
 *   message?: string,
 *   data?: {
 *     id?: string,
 *     messages?: Array<{
 *       id?: string,
 *       role?: string,
 *       status?: string,
 *       content?: any
 *     }>
 *   }
 * }>}
 */
export const getConversation = async (conversationId) => {
  const res = await aiFetch(`conversations/${conversationId}`);
  return res.json();
};

export default { createAssistantMessage, getConversation };
